import {
  type Row,
  type RowData,
  type RowModel,
  type Table,
  tableMemo,
} from '@tanstack/react-table';

import type { TDataTableFeatures } from '../types/features.types.js';
import type { TableMemoTable } from '../types/table.types.js';
import { row_getIsExpanded } from './SubRows.js';

// Overwrite the default `getExpandedRowModel` with our custom implementation that adds support for our implementation of getIsExpanded custom logic.
export function createExpandedRowModel<
  // oxlint-disable-next-line typescript/no-explicit-any
  TData extends RowData = any,
>(): (
  table: Table<TDataTableFeatures, TData>,
) => () => RowModel<TDataTableFeatures, TData> {
  return (table) => {
    return tableMemo({
      feature: 'rowExpandingFeature',
      table: table as unknown as TableMemoTable,
      fnName: 'table.getExpandedRowModel',
      memoDeps: () => [
        table.atoms.expanded?.get(),
        table.getPreExpandedRowModel(),
        table.options.paginateExpandedRows,
      ],
      fn: () => _createExpandedRowModel(table),
    });
  };
}

function _createExpandedRowModel<
  // oxlint-disable-next-line typescript/no-explicit-any
  TData extends RowData = any,
>(
  table: Table<TDataTableFeatures, TData>,
): RowModel<TDataTableFeatures, TData> {
  const rowModel = table.getPreExpandedRowModel();
  const expanded = table.atoms.expanded?.get();

  if (
    !rowModel.rows.length ||
    (expanded !== true && !Object.keys(expanded ?? {}).length)
  ) {
    return rowModel;
  }

  if (!table.options.paginateExpandedRows) {
    // Only expand rows at this point if they are being paginated
    return rowModel;
  }

  return expandRows(rowModel);
}

export function expandRows<
  // oxlint-disable-next-line typescript/no-explicit-any
  TData extends RowData = any,
>(
  rowModel: RowModel<TDataTableFeatures, TData>,
): RowModel<TDataTableFeatures, TData> {
  const expandedRows: Array<Row<TDataTableFeatures, TData>> = [];

  const handleRow = (row: Row<TDataTableFeatures, TData>) => {
    expandedRows.push(row);

    if (row.subRows.length && row_getIsExpanded(row)) {
      row.subRows.forEach(handleRow);
    }
  };

  rowModel.rows.forEach(handleRow);

  return {
    rows: expandedRows,
    flatRows: rowModel.flatRows,
    rowsById: rowModel.rowsById,
  };
}
